// Apolo IDE — agent retry / crash-reconnect (sprint OpenClaw Harvest, E3). PURE.
//
// Adapts ACPX's "dead agent process detected → session resumed" to Apolo's
// renderer agent loop. The cloud chatFn (POST /agent-turn) THROWS on a network
// failure (the call never reached/returned from the server — a true "crash"),
// but RETURNS { ok:false } on an HTTP error (the server responded → it ran).
// We only retry the THROWN/network case, carrying the SAME idempotency key so
// the server can dedupe if a partial run happened. We NEVER retry a user abort,
// a hit deadline, or an { ok:false } result (would risk double execution/charge).
//
// PURE: no fetch/DOM/React. The loop wires these into its send path.

export const MAX_LLM_RETRIES = 2;
export const RETRY_BASE_DELAY_MS = 600;

/** New idempotency key for a turn; reused across that turn's retries. */
export function makeIdempotencyKey() {
  try {
    return crypto.randomUUID();
  } catch {
    return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** Abort / deadline errors are intentional stops — never retried. */
export function isAbortLike(err) {
  if (!err) return false;
  const name = err.name || '';
  const msg = (err.message || '').toLowerCase();
  return (
    name === 'AbortError' ||
    msg.includes('abort') ||
    msg.includes('cancel') ||
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('deadline')
  );
}

/**
 * Retryable = a network-y failure where the call did NOT get a server response
 * (so re-sending with the same idempotency key is safe). Conservative on purpose.
 */
export function isRetryableError(err) {
  if (!err || isAbortLike(err)) return false;
  const name = err.name || '';
  const msg = (err.message || '').toLowerCase();
  return (
    name === 'TypeError' || // fetch() network failure
    msg.includes('fetch failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('econnreset') ||
    msg.includes('econnrefused') ||
    msg.includes('enotfound') ||
    msg.includes('socket hang up') ||
    msg.includes('502') ||
    msg.includes('503') ||
    msg.includes('504')
  );
}

/**
 * Retry policy. `attempt` = number of attempts already FAILED (1-based after the
 * first failure). Exponential backoff from baseDelayMs.
 * @returns {{ retry: boolean, delayMs: number }}
 */
export function resolveRetry(attempt, { maxRetries = MAX_LLM_RETRIES, baseDelayMs = RETRY_BASE_DELAY_MS } = {}) {
  if (!Number.isFinite(attempt) || attempt >= maxRetries) return { retry: false, delayMs: 0 };
  const delayMs = baseDelayMs * Math.pow(2, Math.max(0, attempt - 1));
  return { retry: true, delayMs };
}

/** Sleep that resolves early if the signal aborts (so Stop stays responsive). */
export function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener?.('abort', () => {
      clearTimeout(t);
      resolve();
    }, { once: true });
    return undefined;
  });
}
